import { useEffect, type ReactNode } from "react";
import { DateTime } from "luxon";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ILead } from "../../../models/Lead";
import { useAuth } from "../../../contexts/AuthContext";
import useSalvage from "../../../data/useSalvage";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import usePricingAccess from "../../../hooks/usePricingAccess";
import { useUserLookup } from "../../../hooks/useUserLookup";
import { CheckboxInput } from "../../shared/Inputs/CheckboxInput";
import { NumberInput } from "../../shared/Inputs/NumberInput";
import LoadingWheel from "../../shared/LoadingWheel";
import PriceFormat from "../../../utils/priceFormat";
import classNames from "../../../utils/classNames";
import { salvageDaysInStock, salvageProfit } from "../../../utils/salvageMath";

function bookedByName(findUser: (userId: string) => { fullname?: string; forename?: string; surname?: string } | undefined, userId?: string | null) {
  if (!userId) return null;
  const user = findUser(userId);
  if (!user) return null;
  const name = user.fullname?.trim() || `${user.forename ?? ""} ${user.surname ?? ""}`.trim();
  return name || null;
}

type FormValues = {
  copartInstructed: boolean;
  copartCollectedOn: string;
  collectionCost: string;
  capValue: string;
  proquoteValue: string;
  projectedAuctionValue: string;
  auctionRun1: string;
  auctionRun2: string;
  auctionRun3: string;
  auctionRun4: string;
  actualAuctionValue: string;
  soldOn: string;
  finalSellingFees: string;
  vatCharges: string;
  returnedToGp: boolean;
  redeliveryCost: string;
};

const emptyForm: FormValues = {
  copartInstructed: false,
  copartCollectedOn: "",
  collectionCost: "",
  capValue: "",
  proquoteValue: "",
  projectedAuctionValue: "",
  auctionRun1: "",
  auctionRun2: "",
  auctionRun3: "",
  auctionRun4: "",
  actualAuctionValue: "",
  soldOn: "",
  finalSellingFees: "",
  vatCharges: "",
  returnedToGp: false,
  redeliveryCost: "",
};

function money(value: string): number | null {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function asDay(value?: string | null): string {
  return value ? value.slice(0, 10) : "";
}

function asInput(value?: number | null): string {
  return value == null ? "" : String(value);
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</h3>
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function MoneyRow({
  label,
  amount,
  deduct,
  strong,
  canViewPrices,
}: {
  label: string;
  amount: number | null;
  deduct?: boolean;
  strong?: boolean;
  canViewPrices: boolean;
}) {
  const shown = !canViewPrices || amount == null ? "—" : deduct ? `− ${PriceFormat(amount)}` : PriceFormat(amount);
  return (
    <div className={classNames("flex items-baseline justify-between gap-4 text-sm", strong && "pt-2")}>
      <span className={classNames(strong ? "font-semibold text-gray-900" : "text-gray-600")}>{label}</span>
      <span className={classNames(
        "tabular-nums",
        strong ? "text-base font-semibold" : "text-gray-900",
        strong && amount != null && amount >= 0 && canViewPrices && "text-emerald-700",
        strong && amount != null && amount < 0 && canViewPrices && "text-red-700"
      )}>
        {shown}
      </span>
    </div>
  );
}

export default function LeadSalvageTab({ lead }: { lead: ILead }) {
  const { t } = useTranslation("leads");
  const { customerId } = useAuth();
  const { canViewPrices } = usePricingAccess();
  const { salvage, save } = useSalvage(customerId, lead.id, "LeadSalvageTab");
  const { saveHandlers } = useDefaultCRUDHandlers("Salvage");
  const { findUser } = useUserLookup();
  const bookedBy = bookedByName(findUser, lead.vehicle?.bookedCollectionBy);
  const { register, handleSubmit, reset, watch } = useForm<FormValues>({ defaultValues: emptyForm });

  useEffect(() => {
    const row = salvage.data?.data;
    if (!row) return;
    reset({
      copartInstructed: !!row.copartInstructed,
      copartCollectedOn: asDay(row.copartCollectedOn),
      collectionCost: asInput(row.collectionCost),
      capValue: asInput(row.capValue),
      proquoteValue: asInput(row.proquoteValue),
      projectedAuctionValue: asInput(row.projectedAuctionValue),
      auctionRun1: asInput(row.auctionRun1),
      auctionRun2: asInput(row.auctionRun2),
      auctionRun3: asInput(row.auctionRun3),
      auctionRun4: asInput(row.auctionRun4),
      actualAuctionValue: asInput(row.actualAuctionValue),
      soldOn: asDay(row.soldOn),
      finalSellingFees: asInput(row.finalSellingFees),
      vatCharges: asInput(row.vatCharges),
      returnedToGp: !!row.returnedToGp,
      redeliveryCost: asInput(row.redeliveryCost),
    });
  }, [reset, salvage.data?.data]);

  const watched = watch();
  const vehiclePrice = salvage.data?.data?.vehiclePrice ?? lead.price ?? 0;
  const daysInStock = salvageDaysInStock(
    watched.copartCollectedOn || null,
    watched.soldOn || null,
    undefined,
    watched.returnedToGp
      ? salvage.data?.data?.returnedOn || DateTime.utc().toISO()
      : salvage.data?.data?.returnedOn
  );
  const sellingPrice = money(watched.actualAuctionValue);
  const collectionCost = money(watched.collectionCost);
  const sellingFees = money(watched.finalSellingFees);
  const vatCharges = money(watched.vatCharges);
  const redeliveryCost = money(watched.redeliveryCost);
  const returnedToGp = !!watched.returnedToGp;
  const sold = sellingPrice != null && !returnedToGp;
  const profit = returnedToGp
    ? null
    : salvageProfit(sellingPrice, collectionCost, sellingFees, vatCharges, vehiclePrice);
  const vehicleLabel = [lead.vehicle?.vrm, lead.vehicle?.combinedMake, lead.vehicle?.combinedModel].filter(Boolean).join(" ");

  const onSubmit = (values: FormValues) => {
    save.mutate(
      {
        copartInstructed: !!values.copartInstructed,
        copartCollectedOn: values.copartCollectedOn || null,
        collectionCost: money(values.collectionCost),
        capValue: money(values.capValue),
        proquoteValue: money(values.proquoteValue),
        projectedAuctionValue: money(values.projectedAuctionValue),
        auctionRun1: money(values.auctionRun1),
        auctionRun2: money(values.auctionRun2),
        auctionRun3: money(values.auctionRun3),
        auctionRun4: money(values.auctionRun4),
        actualAuctionValue: money(values.actualAuctionValue),
        soldOn: values.soldOn || null,
        finalSellingFees: money(values.finalSellingFees),
        vatCharges: money(values.vatCharges),
        returnedToGp: !!values.returnedToGp,
        redeliveryCost: money(values.redeliveryCost),
      },
      saveHandlers
    );
  };

  if (salvage.isLoading) {
    return (
      <div className="flex items-center py-[10vh] bg-white rounded-lg shadow">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-4 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-100 pb-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{t("Vehicle")}</p>
          <p className="mt-1 text-lg font-semibold text-gray-900">{vehicleLabel || "—"}</p>
          <p className="mt-1 text-sm text-gray-500">
            {t("Booked by")} {bookedBy || "—"}
            {daysInStock != null && ` · ${daysInStock} ${t("days in stock")}`}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            {returnedToGp ? t("Returned to GP") : t("Profit")}
          </p>
          <p className={classNames(
            "mt-1 text-2xl font-semibold tabular-nums",
            canViewPrices && profit != null && profit >= 0 && "text-emerald-700",
            canViewPrices && profit != null && profit < 0 && "text-red-700",
            (profit == null || !canViewPrices) && "text-gray-400"
          )}>
            {returnedToGp
              ? canViewPrices && redeliveryCost != null
                ? PriceFormat(redeliveryCost)
                : "—"
              : canViewPrices && profit != null
                ? PriceFormat(profit)
                : "—"}
          </p>
          {returnedToGp ? (
            <p className="mt-1 text-xs text-gray-500">{t("Did not sell at auction")}</p>
          ) : (
            profit == null && (
              <p className="mt-1 text-xs text-gray-500">{t("Enter the selling price to lock profit")}</p>
            )
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <Section title={t("Copart collection")} hint={t("When Copart take the vehicle from us")}>
            <CheckboxInput
              label={t("Copart instructed")}
              register={register}
              registerName="copartInstructed"
            />
            <label>
              <span className="text-sm font-medium text-gray-500">{t("Date collected")}</span>
              <input
                type="date"
                {...register("copartCollectedOn")}
                className="mt-1 w-full border-gray-300 text-sm text-gray-900"
              />
            </label>
            <NumberInput label={t("Collection cost")} register={register} registerName="collectionCost" disabled={!canViewPrices} />
            <div>
              <p className="text-sm font-medium text-gray-500">{t("Days in stock")}</p>
              <p className="mt-2 text-sm text-gray-900">{daysInStock == null ? "—" : daysInStock}</p>
            </div>
          </Section>

          <Section title={t("Guide values")} hint={t("What we think it is worth before it goes through")}>
            <NumberInput label={t("CAP")} register={register} registerName="capValue" disabled={!canViewPrices} />
            <NumberInput label={t("Retail")} register={register} registerName="proquoteValue" disabled={!canViewPrices} />
            <NumberInput label={t("Projected auction")} register={register} registerName="projectedAuctionValue" disabled={!canViewPrices} />
          </Section>

          <Section title={t("Auction")} hint={t("Record each run. The selling price is the one that sold.")}>
            <NumberInput label={t("Run 1")} register={register} registerName="auctionRun1" disabled={!canViewPrices} />
            <NumberInput label={t("Run 2")} register={register} registerName="auctionRun2" disabled={!canViewPrices} />
            <NumberInput label={t("Run 3")} register={register} registerName="auctionRun3" disabled={!canViewPrices} />
            <NumberInput label={t("Run 4")} register={register} registerName="auctionRun4" disabled={!canViewPrices} />
            <NumberInput label={t("Selling price")} register={register} registerName="actualAuctionValue" disabled={!canViewPrices} />
            <label>
              <span className="text-sm font-medium text-gray-500">{t("Date sold")}</span>
              <input
                type="date"
                {...register("soldOn")}
                className="mt-1 w-full border-gray-300 text-sm text-gray-900"
              />
            </label>
            <NumberInput label={t("Selling fees")} register={register} registerName="finalSellingFees" disabled={!canViewPrices} />
            <NumberInput label={t("VAT")} register={register} registerName="vatCharges" disabled={!canViewPrices} />
          </Section>

          <Section title={t("Returned")} hint={t("Did not sell at auction and came back to us")}>
            <CheckboxInput
              label={t("Did not sell, returned to GP")}
              register={register}
              registerName="returnedToGp"
            />
            <NumberInput
              label={t("Redelivery cost")}
              register={register}
              registerName="redeliveryCost"
              disabled={!canViewPrices}
            />
          </Section>
        </div>

        <aside className="xl:col-span-1">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              {returnedToGp ? t("Returned") : t("Profit")}
            </h3>
            <p className="mt-1 text-xs text-gray-500">
              {returnedToGp
                ? t("No sale. Record the cost to bring it back.")
                : t("Selling price minus every cost and what we paid")}
            </p>
            <div className="mt-4 space-y-2">
              {returnedToGp ? (
                <>
                  <MoneyRow label={t("Redelivery cost")} amount={redeliveryCost} canViewPrices={canViewPrices} />
                  <MoneyRow label={t("Purchase price")} amount={canViewPrices ? vehiclePrice : null} deduct canViewPrices={canViewPrices} />
                </>
              ) : (
                <>
                  <MoneyRow label={t("Selling price")} amount={sellingPrice} canViewPrices={canViewPrices} />
                  <MoneyRow label={t("Collection cost")} amount={sold ? collectionCost ?? 0 : collectionCost} deduct canViewPrices={canViewPrices} />
                  <MoneyRow label={t("Selling fees")} amount={sold ? sellingFees ?? 0 : sellingFees} deduct canViewPrices={canViewPrices} />
                  <MoneyRow label={t("VAT")} amount={sold ? vatCharges ?? 0 : vatCharges} deduct canViewPrices={canViewPrices} />
                  <MoneyRow label={t("Purchase price")} amount={canViewPrices ? vehiclePrice : null} deduct canViewPrices={canViewPrices} />
                  <div className="border-t border-gray-200" />
                  <MoneyRow label={t("Profit")} amount={profit} strong canViewPrices={canViewPrices} />
                </>
              )}
            </div>
            <button
              type="submit"
              disabled={save.isLoading}
              className="mt-4 w-full inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-60"
            >
              {t("Save")}
            </button>
          </div>
        </aside>
      </div>
    </form>
  );
}
